/**
 * SoundtrackRangePill — выбор фрагмента ролика для саундтрека (Mureka).
 *
 * Mureka умеет писать музыку не под весь ролик, а под его кусок
 * (audio_start/audio_end). Пилюля появляется только когда загружено ВИДЕО:
 * у фото нет длительности, и выбирать там нечего. Минимальный кусок — 3
 * секунды (требование Mureka), поэтому границы не дают сойтись ближе:
 * невалидный диапазон просто нельзя собрать руками, а не «оплатил → отказ».
 *
 * Чрома поповера и классы слайдера — общие с остальными пилюлями настроек.
 */
import BottomBarPopover from './BottomBarPopover'
import { Icon } from '../../../../public/icons/Icon.tsx'

/** Минимальная длина фрагмента, которую принимает Mureka. */
const MIN_SPAN_SEC = 3

function fmt(sec: number): string {
	const s = Math.max(0, Math.round(sec))
	return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

interface Props {
	/** Длительность загруженного видео в секундах. */
	duration: number
	startSec?: number
	endSec?: number
	onChange: (start: number | undefined, end: number | undefined) => void
	disabled?: boolean
}

export default function SoundtrackRangePill({
	duration,
	startSec,
	endSec,
	onChange,
	disabled,
}: Props) {
	// Ролик короче минимального фрагмента — выбирать нечего.
	if (!Number.isFinite(duration) || duration < MIN_SPAN_SEC) return null

	const total = Math.floor(duration)
	const hasRange = startSec != null && endSec != null
	const start = hasRange ? startSec : 0
	const end = hasRange ? endSec : total

	const apply = (nextStart: number, nextEnd: number) => {
		// Держим границы в пределах ролика и не даём им сойтись ближе минимума.
		const s = Math.min(Math.max(0, nextStart), total - MIN_SPAN_SEC)
		const e = Math.max(Math.min(total, nextEnd), s + MIN_SPAN_SEC)
		onChange(s, e)
	}

	return (
		<BottomBarPopover
			tooltip="Под какой кусок ролика писать музыку"
			sectionTitle="Фрагмент"
			icon={<Icon id="gen-duration" size={20} />}
			label={hasRange ? `${fmt(start)} – ${fmt(end)}` : 'Весь ролик'}
			disabled={disabled}
			width={248}
			panel={
				<div className="bbar-section__slider">
					<div className="bbar-popover__slider-row">
						<span className="bbar-popover__slider-val">Начало</span>
						<span className="bbar-popover__slider-range">{fmt(start)}</span>
					</div>
					<input
						type="range"
						className="bbar-slider bbar-slider--full"
						min={0}
						max={Math.max(0, total - MIN_SPAN_SEC)}
						step={1}
						value={start}
						onChange={(e) => apply(Number(e.target.value), end)}
						disabled={disabled}
					/>
					<div className="bbar-popover__slider-row">
						<span className="bbar-popover__slider-val">Конец</span>
						<span className="bbar-popover__slider-range">{fmt(end)}</span>
					</div>
					<input
						type="range"
						className="bbar-slider bbar-slider--full"
						min={MIN_SPAN_SEC}
						max={total}
						step={1}
						value={end}
						onChange={(e) => apply(start, Number(e.target.value))}
						disabled={disabled}
					/>
					{hasRange && (
						<button
							type="button"
							className="bbar-section__chip"
							onClick={() => onChange(undefined, undefined)}
							disabled={disabled}
						>
							Весь ролик
						</button>
					)}
				</div>
			}
		/>
	)
}
